import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, LogOut } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/properties", label: "Properties" },
    { path: "/about", label: "About" },
    { path: "/contact", label: "Contact" },
    { path: "/schedule-visit", label: "Schedule Visit" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "backdrop-blur-xl bg-[#051C2C]/80 border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" data-testid="navbar-logo" className="flex items-center gap-2">
            <span className="font-cursive text-3xl text-gold">Noor</span>
            <span className="font-heading text-lg tracking-wider text-beige uppercase">Realtor</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`text-sm tracking-widest uppercase font-body hover-underline transition-colors duration-300 ${
                  isActive(link.path)
                    ? "text-gold"
                    : "text-beige/70 hover:text-beige"
                }`}
              >
                {link.label}
              </Link>
            ))}
            {user ? (
              <div className="flex items-center gap-3">
                <Link
                  to="/admin"
                  data-testid="nav-link-admin"
                  className={`text-xs tracking-widest uppercase transition-colors duration-300 ${
                    isActive("/admin") ? "text-gold" : "text-beige/50 hover:text-gold"
                  }`}
                >
                  Admin
                </Link>
                <button
                  onClick={logout}
                  data-testid="nav-logout-button"
                  className="text-beige/40 hover:text-red-400 transition-colors"
                  title="Logout"
                >
                  <LogOut size={16} />
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                data-testid="nav-link-login"
                className="text-xs tracking-widest uppercase text-beige/40 hover:text-gold transition-colors duration-300"
              >
                Admin
              </Link>
            )}
          </div>

          {/* Mobile Toggle */}
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden text-beige"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-navy-light/95 backdrop-blur-xl border-t border-white/10"
        >
          <div className="px-6 py-6 space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`block text-sm tracking-widest uppercase ${
                  isActive(link.path) ? "text-gold" : "text-beige/70"
                }`}
              >
                {link.label}
              </Link>
            ))}
            {user ? (
              <>
                <Link to="/admin" className="block text-sm tracking-widest uppercase text-beige/50">Admin</Link>
                <button onClick={logout} className="block text-sm tracking-widest uppercase text-red-400/60">Logout</button>
              </>
            ) : (
              <Link to="/login" className="block text-xs tracking-widest uppercase text-beige/40">Admin Login</Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
